import {Action, Selector, State, StateContext} from '@ngxs/store';
import {
  ClearState,
  FetchCampaignPrices,
  UpdateAccount,
  UpdateBillingSchemes,
  UpsertCampaignPricing,
} from 'src/app/shared/state/session.actions';
import {inject, Injectable} from '@angular/core';
import {User} from 'src/app/shared/models/user.model';
import {BillingSchemeModel} from 'src/app/modules/campaigns/models/billing-scheme.model';
import {CampaignPriceModel} from 'src/app/modules/campaigns/models/campaign-price.model';
import {UserService} from 'src/app/shared/auth/services/user.service';
import {BillingSchemeService} from 'src/app/modules/campaigns/services/billing-scheme.service';
import {CampaignsService} from 'src/app/modules/campaigns/services/campaigns.service';
import {tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';

export interface SessionStateModel {
  account: User | undefined;
  billingSchemes: BillingSchemeModel[] | undefined;
  campaignPrices: CampaignPriceModel[] | undefined;
}

export const sessionStateDefaults: SessionStateModel = {
  account: undefined,
  billingSchemes: undefined,
  campaignPrices: undefined,
};

@State<SessionStateModel>({
  name: 'session',
  defaults: sessionStateDefaults,
})

@Injectable()
export class SessionState {
  private userService = inject(UserService);
  private billingSchemeService = inject(BillingSchemeService);
  private campaignsService = inject(CampaignsService);

  @Selector()
  static account(state: SessionStateModel): User | undefined {
    return state.account;
  }

  @Selector()
  static billingSchemes(state: SessionStateModel): BillingSchemeModel[] | undefined {
    return state.billingSchemes;
  }

  @Selector()
  static campaignPrices(state: SessionStateModel): CampaignPriceModel[] | undefined {
    return state.campaignPrices;
  }

  @Action(UpdateAccount)
  updateAccount(ctx: StateContext<SessionStateModel>) {
    return this.userService.fetch().pipe(
      tap(user => ctx.patchState({account: user}))
    );
  }

  @Action(ClearState)
  clearState(ctx: StateContext<SessionStateModel>) {
    ctx.setState({...sessionStateDefaults});
  }

  @Action(UpdateBillingSchemes)
  updateBillingSchemes(ctx: StateContext<SessionStateModel>): Observable<BillingSchemeModel[]> {
    const currentState = ctx.getState();
    if (currentState.billingSchemes) {
      return of(currentState.billingSchemes);
    }

    return this.billingSchemeService.fetch().pipe(
      tap(schemes => ctx.patchState({billingSchemes: schemes}))
    );
  }

  @Action(FetchCampaignPrices)
  fetchCampaignPrices(ctx: StateContext<SessionStateModel>): Observable<CampaignPriceModel[]> {
    const currentState = ctx.getState();
    if (currentState.campaignPrices) {
      return of(currentState.campaignPrices);
    }

    return this.campaignsService.fetch().pipe(
      tap(prices => ctx.patchState({campaignPrices: prices}))
    );
  }

  @Action(UpsertCampaignPricing)
  upsertCampaignPricing(ctx: StateContext<SessionStateModel>, action: UpsertCampaignPricing): Observable<CampaignPriceModel> {
    const serviceCall = action.priceId
      ? this.campaignsService.update(action.priceId, action.data)
      : this.campaignsService.add(action.data);

    return serviceCall.pipe(
      tap(updatedCampaign => {
        const currentState = ctx.getState();
        const currentPrices = currentState.campaignPrices || [];

        if (action.priceId) {
          // Update existing campaign
          const index = currentPrices.findIndex(c => c.price_id === action.priceId);
          if (index !== -1) {
            const newPrices = [...currentPrices];
            newPrices[index] = updatedCampaign;
            ctx.patchState({campaignPrices: newPrices});
          }
        } else {
          // Add new campaign
          const newPrices = [...currentPrices, updatedCampaign];
          ctx.patchState({campaignPrices: newPrices});
        }
      })
    );
  }

}
